import { Link } from 'react-router-dom';

const SURFACES = [
  {
    title: 'Architect',
    body: 'Your main browser workspace. Ask questions, work with plans, and use the built-in Config, Schedules, and Status tabs.',
  },
  {
    title: 'Explorer',
    body: 'A visual map of the project. Use it when you want to understand relationships, search the system, or inspect tensions.',
  },
  {
    title: 'Dashboard',
    body: 'A separate route for operational information, configuration, schedules, and documentation when you need it.',
  },
];

const PATHS = [
  'Start an empty project and let Architect help shape the first useful slice.',
  'Read an existing repository without rewriting your application code.',
  'Map a large multi-repo system from one project-bound browser workspace.',
  'Use Codex CLI inline, Anthropic API, OpenAI, Ollama, or LM Studio.',
];

export function Home() {
  return (
    <>
      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">DreamGraph v14.0.2 - Ashoka</p>
          <h1>Understand and evolve your project from one browser workspace.</h1>
          <p className="hero__lede">
            DreamGraph gives your coding assistant durable project context with reviewable cognitive state. Start the daemon, open
            Architect, and work in normal language. Config, Schedules, and Status are inside Architect;
            open Explorer in another tab when you want to inspect the graph.
          </p>
          <div className="hero__cta">
            <Link to="/guide/easy-start" className="btn btn--primary">Start in a few minutes</Link>
            <Link to="/download" className="btn btn--ghost">Install DreamGraph</Link>
          </div>
          <pre className="hero__install" aria-label="First project commands">
            <code>{`dg init --name my-app --project . --transport http\ndg start my-app --http\ndg scan my-app --depth deep`}</code>
          </pre>
        </div>
        <div className="hero__visual">
          <img
            src="/images/explorer-3d-screenshot-3.png"
            alt="DreamGraph Explorer view"
            width={600}
            height={354}
          />
        </div>
      </section>

      <section className="section install-card">
        <h2>Computer Use, connected to your browser</h2>
        <p>Codex CLI and native API routes now share DreamGraph’s browser backend. Connect the extension to work with existing tabs and handle Windows Save/Open dialogs. Context-cost fixes keep repeated tool work smaller.</p>
        <Link to="/guide/computer-use" className="btn btn--ghost">Install and connect the browser extension</Link>
      </section>

      <section className="section qa-strip">
        <div className="qa-grid">
          <article className="qa">
            <span className="qa__num">1</span>
            <h3>Install</h3>
            <p>Install once and create an instance for your project.</p>
          </article>
          <article className="qa">
            <span className="qa__num">2</span>
            <h3>Open Architect</h3>
            <p>Use its operational tabs, then open Explorer separately when useful.</p>
          </article>
          <article className="qa">
            <span className="qa__num">3</span>
            <h3>Start working</h3>
            <p>Tell Architect what you want to understand, plan, or change.</p>
          </article>
        </div>
      </section>

      <section className="section">
        <h2 className="section__title">Connected browser surfaces, simple roles</h2>
        <div className="pillars">
          {SURFACES.map((surface) => (
            <article key={surface.title} className="pillar">
              <h3>{surface.title}</h3>
              <p>{surface.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section section--alt">
        <h2 className="section__title">One setup guide for the common paths</h2>
        <ul className="capabilities">
          {PATHS.map((path) => <li key={path}>{path}</li>)}
        </ul>
        <div className="hero__cta">
          <Link to="/guide/easy-start" className="btn btn--primary">Read Easy Start</Link>
          <a href="/docs/easy-start.pdf" className="btn btn--ghost">Download PDF</a>
        </div>
      </section>
    </>
  );
}
